import React from 'react';

function TerminCalendar({ termine, currentDate, onDateClick, onTerminClick, onMonthChange }) {

  // SICHERHEITSPRÜFUNG: Termine validieren und bereinigen
  const safeTermine = React.useMemo(() => {
    if (!Array.isArray(termine)) {
      console.warn('termine ist kein Array:', termine);
      return [];
    }

    const validTermine = termine.filter(termin => {
      if (!termin || typeof termin !== 'object') {
        console.warn('Ungültiger Termin (kein Objekt):', termin);
        return false;
      }

      if (!termin.datum) {
        console.warn('Termin ohne datum:', termin);
        return false;
      }

      if (!termin.uhrzeit) {
        console.warn('Termin ohne uhrzeit:', termin);
        return false;
      }

      // Datum-Format validieren
      try {
        new Date(termin.datum);
      } catch (error) {
        console.warn('Ungültiges Datum-Format:', termin.datum, termin);
        return false;
      }

      return true;
    });

    return validTermine;
  }, [termine]);

  // Memoize Wochentage-Berechnung
  const weekDates = React.useMemo(() => {
    const week = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      week.push(currentDay);
    }

    return week;
  }, [currentDate]);

  // Memoize Zeitslots
  const timeSlots = React.useMemo(() => {
    const slots = [];
    for (let hour = 8; hour <= 21; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:15`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
      slots.push(`${hour.toString().padStart(2, '0')}:45`);
    }
    slots.push('22:00');
    return slots;
  }, []);

  // Memoize Termine nach Datum und Zeit für bessere Performance
  const termineByDateTime = React.useMemo(() => {
    const map = new Map();

    safeTermine.forEach(termin => {
      try {
        const terminDateParts = termin.datum.split('-');
        const terminDate = new Date(
          parseInt(terminDateParts[0]),
          parseInt(terminDateParts[1]) - 1,
          parseInt(terminDateParts[2])
        );
        const terminDateString = terminDate.toLocaleDateString('en-CA');
        const terminTime = termin.uhrzeit.substring(0, 5);

        const key = `${terminDateString}-${terminTime}`;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(termin);
      } catch (error) {
        console.error('Fehler beim Verarbeiten des Termins:', termin, error);
      }
    });

    return map;
  }, [safeTermine]);

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  // Zoom: Zeilenhöhe State
  const [rowHeight, setRowHeight] = React.useState(25); // Standardhöhe 25px

  // Zoom-Handler: Shift+Scroll
  React.useEffect(() => {
    const handleWheel = (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        setRowHeight((prev) => {
          // Umgekehrte Richtung: Hoch = größer, Runter = kleiner
          let next = prev + (e.deltaY < 0 ? 5 : -5);
          if (next < 25) next = 25;
          if (next > 100) next = 100;
          return next;
        });
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  // Scrollen im Kalender unterdrücken, wenn Shift gehalten wird
  React.useEffect(() => {
    const scrollContainer = document.querySelector('.calendar-scroll-container');
    if (!scrollContainer) return;
    const preventScroll = (e) => {
      if (e.shiftKey) {
        e.preventDefault();
      }
    };
    scrollContainer.addEventListener('wheel', preventScroll, { passive: false });
    return () => scrollContainer.removeEventListener('wheel', preventScroll);
  }, []);

  // Berechne die Endzeit eines Termins
  const calculateEndTime = React.useCallback((startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  }, []);

  // Standard Termin Zeit
  const getTerminDuration = React.useCallback((termin) => {
    // Wenn keine Dauer angegeben ist, Standard 1 Stunde (60 Minuten)
    const durationMinutes = termin.dauer || 60;

    // Konvertiere Minuten in Slots (1 Slot = 15 Minuten)
    return Math.ceil(durationMinutes / 15);
  }, []);

  // Optimierte Funktion zum Abrufen der Termine für ein bestimmtes Datum und Zeit
  const getTermineForDateTime = React.useCallback((date, timeSlot) => {
    if (!date) return [];

    const dateString = date.toLocaleDateString('en-CA');
    const key = `${dateString}-${timeSlot}`;

    return termineByDateTime.get(key) || [];
  }, [termineByDateTime]);

  // Neue Funktion zum Berechnen der Position und Breite der Termine
  const calculateTerminPosition = React.useCallback((termine, index) => {
    const totalTermine = termine.length;
    if (totalTermine <= 1) return { left: '2px', right: '2px', width: 'calc(100% - 4px)' };

    const width = `calc(${100 / totalTermine}% - 4px)`;
    const left = `${(index * 100) / totalTermine}%`;

    return {
      left: `calc(${left} + 2px)`,
      width: width
    };
  }, []);





  const getTerminColor = React.useCallback((index) => {
    const colors = ['#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3'];
    return colors[index % colors.length];
  }, []);

  const previousWeek = React.useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    onMonthChange(newDate);
  }, [currentDate, onMonthChange]);

  const nextWeek = React.useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    onMonthChange(newDate);
  }, [currentDate, onMonthChange]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Berechne Wochenbereich für Anzeige
  const startDate = weekDates[0];
  const endDate = weekDates[6];
  const formatDateRange = React.useCallback(() => {
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startMonth = monthNames[startDate.getMonth()];
    const endMonth = monthNames[endDate.getMonth()];

    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDay} - ${endDay} ${startMonth}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
  }, [startDate, endDate, monthNames]);

  console.log("Das hier sind die Termine aus dem Calender", termine);

  return (
    <div className="termin-calendar-week">
      <div className="calendar-header">
        <button onClick={previousWeek} className="month-nav-button">
          ←
        </button>
        <h3>
          {formatDateRange()}
        </h3>
        <button onClick={nextWeek} className="month-nav-button">
          →
        </button>
        <div style={{ marginLeft: 'auto', fontSize: '0.9rem', color: '#4a5568' }}>
          <span style={{ fontWeight: 600 }}>Zoom:</span> <span>{rowHeight}px</span> <span style={{ fontSize: '0.8em' }}>(Shift+Scroll)</span>
        </div>
      </div>

      {/* Fixierte Wochentage Header */}
      <div className="weekdays-header-fixed">
        <div className="time-header-fixed"></div>
        {weekDates.map((date, index) => {
          const isToday = date.getTime() === today.getTime();
          return (
            <div key={index} className={`weekday-header-fixed ${isToday ? 'today' : ''}`}>
              <div className="weekday-name">{weekDays[index]}</div>
              <div className="weekday-date">{date.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* Scrollbarer Kalender Container */}
      <div className="calendar-scroll-container">
        <div className="calendar-week-grid">
          {/* Zeitslots und Termine */}
          {timeSlots.map((timeSlot, timeIndex) => (
            <React.Fragment key={timeSlot}>
              {/* Zeitslot Label */}
              <div className="time-slot-label" style={{ height: `${rowHeight}px` }}>
                {timeSlot}
              </div>

              {/* Termine für jeden Tag in diesem Zeitslot */}
              {weekDates.map((date, dayIndex) => {
                const dayTermine = getTermineForDateTime(date, timeSlot);
                const isPast = date < today || (date.getTime() === today.getTime() && new Date().getHours() >= parseInt(timeSlot));

                return (
                  <div
                    key={`${timeSlot}-${dayIndex}`}
                    className={`time-slot ${isPast ? 'past' : ''}`}
                    style={{ height: `${rowHeight}px` }}
                    onClick={() => onDateClick && onDateClick(date, timeSlot)}
                  >
                    {dayTermine.map((termin, terminIndex) => {
                      const duration = getTerminDuration(termin);
                      const color = getTerminColor(terminIndex);
                      const position = calculateTerminPosition(dayTermine, terminIndex);
                      const endTime = calculateEndTime(termin.uhrzeit, termin.dauer || 60);

                      return (
                        <div
                          key={termin.id}
                          className="termin-block-week"
                          style={{
                            backgroundColor: color,
                            height: `${duration * rowHeight - 2}px`,
                            zIndex: 10,
                            left: position.left,
                            width: position.width,
                            right: 'auto'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onTerminClick) {
                              onTerminClick(termin.id);
                            }
                          }}
                          title={`${termin.uhrzeit} - ${endTime} - ${termin.kunde_vorname} (${termin.mitarbeiter_name || 'Kein Berater zugewiesen'})`}
                        >
                          <div className="termin-time-week">
                            {termin.uhrzeit.substring(0, 5)}
                          </div>
                          <div className="termin-kunde-week">
                            {termin.kunde_vorname || 'Unbekannter Kunde'}
                          </div>
                          <div className="termin-details-week">
                            {termin.mitarbeiter_name || 'Kein Berater zugewiesen'}
                            {termin.plz && (
                              <div className="termin-plz-week">
                                {termin.plz}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <style jsx>{`
        .termin-calendar-week {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
        }

        .calendar-header {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          gap: 20px;
          flex-shrink: 0;
          position: relative;
          z-index: 5;
        }

        .calendar-header h3 {
          margin: 0;
          font-size: 1.5rem;
          color: #2d3748;
          text-align: center;
          min-width: 200px;
        }

        .month-nav-button {
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          max-width: 40px;
        }

        .month-nav-button:hover {
          background: #3c5ce0;
        }

        .weekdays-header-fixed {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          background: #e2e8f0;
          gap: 1px;
          flex-shrink: 0;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .time-header-fixed {
          background: #f8fafc;
          border-right: 1px solid #e2e8f0;
          height: 60px;
        }

        .weekday-header-fixed {
          background: #4a6de5;
          color: white;
          padding: 15px 8px;
          text-align: center;
          font-weight: 600;
          display: flex;
          flex-direction: column;
          gap: 4px;
          height: 60px;
          justify-content: center;
        }

        .weekday-header-fixed.today {
          background: #e53e3e;
        }

        .calendar-scroll-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          height: 400px;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f7fafc;
        }

        .calendar-scroll-container::-webkit-scrollbar {
          width: 8px;
        }

        .calendar-scroll-container::-webkit-scrollbar-track {
          background: #f7fafc;
        }

        .calendar-scroll-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 4px;
        }

        .calendar-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .calendar-week-grid {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          background: #e2e8f0;
          gap: 1px;
          min-height: 100%;
        }

        .weekday-name {
          font-size: 0.9rem;
        }

        .weekday-date {
          font-size: 1.1rem;
          font-weight: 700;
        }

        .time-slot-label {
          background: #f8fafc;
          padding: 8px;
          text-align: center;
          font-size: 0.85rem;
          color: #4a5568;
          font-weight: 500;
          border-right: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          /* height dynamisch über style */
        }

        .time-slot {
          background: white;
          /* height dynamisch über style */
          cursor: pointer;
          transition: background-color 0.2s;
          position: relative;
          border-bottom: 1px solid #f1f5f9;
        }

        .time-slot:hover {
          background: #f7fafc;
        }

        .time-slot.past {
          background: #f8fafc;
          opacity: 0.7;
        }

        .termin-block-week {
          position: absolute;
          color: white;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.8rem;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          margin: 0 2px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }

        .termin-block-week:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          z-index: 15;
        }

        .termin-time-week {
          font-weight: 700;
          font-size: 0.85rem;
          margin-bottom: 2px;
        }

        .termin-kunde-week {
          font-weight: 600;
          font-size: 0.75rem;
          margin-bottom: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .termin-details-week {
          font-size: 0.7rem;
          opacity: 0.9;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .termin-plz-week {
          font-size: 0.65rem;
          opacity: 0.8;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .calendar-week-grid {
            grid-template-columns: 60px repeat(7, 1fr);
            font-size: 0.8rem;
          }

          .weekdays-header-fixed {
            grid-template-columns: 60px repeat(7, 1fr);
          }

          .time-header-fixed {
            height: 50px;
          }

          .weekday-header-fixed {
            padding: 10px 4px;
            font-size: 0.8rem;
            height: 50px;
          }

          .calendar-scroll-container {
            height: 350px;
          }
          
          .time-slot {
            /* height dynamisch über style */
          }
          
          .time-slot-label {
            /* height dynamisch über style */
            font-size: 0.75rem;
            padding: 4px;
          }
          
          .termin-block-week {
            padding: 4px 6px;
            font-size: 0.7rem;
          }
          
          .calendar-header {
            padding: 15px;
            gap: 15px;
          }
          
          .calendar-header h3 {
            font-size: 1.2rem;
            min-width: 150px;
          }

          .month-nav-button {
            padding: 6px 10px;
            min-width: 36px;
          }
        }

        @media (max-width: 480px) {
          .calendar-week-grid {
            grid-template-columns: 50px repeat(7, 1fr);
          }

          .weekdays-header-fixed {
            grid-template-columns: 50px repeat(7, 1fr);
          }

          .calendar-scroll-container {
            height: 300px;
          }
          
          .termin-kunde-week,
          .termin-details-week,
          .termin-plz-week {
            display: none;
          }
          
          .termin-time-week {
            font-size: 0.7rem;
            text-align: center;
          }

          .calendar-header {
            gap: 10px;
          }

          .calendar-header h3 {
            font-size: 1rem;
            min-width: 120px;
          }

          .month-nav-button {
            padding: 4px 8px;
            min-width: 32px;
            font-size: 0.9rem;
          }

          .time-header-fixed {
            height: 45px;
          }

          .weekday-header-fixed {
            height: 45px;
            padding: 8px 2px;
            font-size: 0.75rem;
          }
          
          .time-slot {
            /* height dynamisch über style */
          }
          
          .time-slot-label {
            /* height dynamisch über style */
            font-size: 0.7rem;
            padding: 2px;
          }
        }
      `}</style>
    </div>
  );
}

export default TerminCalendar;